/**
 * @package @wirklichdigital/query-validator
 *
 * ESM-Plugin zur Validierung und Evaluation von WirklichDigital QueryBuilder-Queries.
 */

const isObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const isArray = Array.isArray;
const isString = (v) => typeof v === 'string';
const isNumber = (v) => typeof v === 'number' && Number.isFinite(v);
const isBoolean = (v) => typeof v === 'boolean';
const isDateLike = (v) => {
    if (v instanceof Date) return !isNaN(v.getTime());
    if (typeof v === 'string' || typeof v === 'number') {
        const d = new Date(v);
        return !isNaN(d.getTime());
    }
    return false;
};

const OP_FLAGS = {
    always: { needsRight: false }, never: { needsRight: false },
    exist: { needsRight: false }, notExist: { needsRight: false },
    isNull: { needsRight: false }, isNotNull: { needsRight: false },
    empty: { needsRight: false }, notEmpty: { needsRight: false },
    isTrue: { needsRight: false }, isFalse: { needsRight: false },
    inArray: { arrayRight: true }, notInArray: { arrayRight: true },
    inList: { arrayRight: true }, notInList: { arrayRight: true },
    containsAny: { arrayRight: true }, notContainsAny: { arrayRight: true },
    between: { between: true }, notBetween: { between: true }
};

const OPERATORS_BY_FIELDTYPE = {
    string: ['equal', 'notEqual', 'inArray', 'notInArray', 'inList', 'notInList', 'contains', 'notContains', 'containsAny', 'notContainsAny', 'startsWith', 'notStartsWith', 'endsWith', 'notEndsWith', 'between', 'notBetween', 'greaterThan', 'greaterEqual', 'lessThan', 'lessEqual', 'always', 'never', 'exist', 'notExist', 'isNull', 'isNotNull', 'empty', 'notEmpty'],
    generic: ['equal', 'notEqual', 'inArray', 'notInArray', 'between', 'notBetween', 'greaterThan', 'greaterEqual', 'lessThan', 'lessEqual', 'always', 'never', 'exist', 'notExist', 'isNull', 'isNotNull', 'empty', 'notEmpty']
};

const FIELDTYPE_ALIASES = { string: 'string', generic: 'generic' };

function resolveFieldTypeKey(type) {
    const t = (type || '').toString().trim();
    return FIELDTYPE_ALIASES[t] || t;
}
function getAllowedOperatorsForType(type) {
    return OPERATORS_BY_FIELDTYPE[resolveFieldTypeKey(type)] || [];
}

function coerceByType(value, type) {
    if (value === null || value === undefined) return value;
    return value;
}

const OP_EVAL = {
    // Null-/Existenz-Checks
    always: () => true,
    never: () => false,
    exist: (left) => left !== undefined && left !== null,
    notExist: (left) => left === undefined || left === null,
    isNull: (left) => left === null || left === undefined,
    isNotNull: (left) => left !== null && left !== undefined,
    empty: (left) => left === '' || left === null || left === undefined || (Array.isArray(left) && left.length === 0),
    notEmpty: (left) => !(Array.isArray(left) ? left.length === 0 : (left === '' || left === null || left === undefined)),
    isTrue: (left) => left === true || left === 1 || left === '1',
    isFalse: (left) => left === false || left === 0 || left === '0',

    // Vergleich (tolerant gegenüber Zahl/String)
    equal: (left, right) => {
        if (Array.isArray(left)) return left.some(l => l == right);
        return left == right;
    },
    notEqual: (left, right) => {
        if (Array.isArray(left)) return left.every(l => l != right);
        return left != right;
    },
    greaterThan: (left, right) => {
        if (Array.isArray(left)) return left.some(l => l > right);
        return left > right;
    },
    greaterEqual: (left, right) => {
        if (Array.isArray(left)) return left.some(l => l >= right);
        return left >= right;
    },
    lessThan: (left, right) => {
        if (Array.isArray(left)) return left.some(l => l < right);
        return left < right;
    },
    lessEqual: (left, right) => {
        if (Array.isArray(left)) return left.some(l => l <= right);
        return left <= right;
    },

    // Bereich
    between: (left, [min, max]) => {
        if (Array.isArray(left)) return left.some(l => l >= min && l <= max);
        return left >= min && left <= max;
    },
    notBetween: (left, [min, max]) => {
        if (Array.isArray(left)) return left.every(l => !(l >= min && l <= max));
        return !(left >= min && left <= max);
    },

    // Mengen/Listen – tolerant für Left=Array und Zahl/String-Mix
    inArray: (left, arr) => {
        const right = Array.isArray(arr) ? arr.map(String) : [String(arr)];
        const leftVals = Array.isArray(left) ? left.map(String) : [String(left)];
        return leftVals.some(l => right.includes(l));
    },
    notInArray: (left, arr) => {
        const right = Array.isArray(arr) ? arr.map(String) : [String(arr)];
        const leftVals = Array.isArray(left) ? left.map(String) : [String(left)];
        return leftVals.every(l => !right.includes(l));
    },
    inList: (left, arr) => {
        const right = Array.isArray(arr) ? arr.map(String) : [String(arr)];
        const leftVals = Array.isArray(left) ? left.map(String) : [String(left)];
        return leftVals.some(l => right.includes(l));
    },
    notInList: (left, arr) => {
        const right = Array.isArray(arr) ? arr.map(String) : [String(arr)];
        const leftVals = Array.isArray(left) ? left.map(String) : [String(left)];
        return leftVals.every(l => !right.includes(l));
    },

    // String-Operatoren – falls left Array ist, trifft "irgendein Element"
    startsWith: (left, right) => {
        const leftVals = Array.isArray(left) ? left : [left];
        const r = String(right);
        return leftVals.some(l => String(l).startsWith(r));
    },
    notStartsWith: (left, right) => {
        const leftVals = Array.isArray(left) ? left : [left];
        const r = String(right);
        return leftVals.every(l => !String(l).startsWith(r));
    },
    endsWith: (left, right) => {
        const leftVals = Array.isArray(left) ? left : [left];
        const r = String(right);
        return leftVals.some(l => String(l).endsWith(r));
    },
    notEndsWith: (left, right) => {
        const leftVals = Array.isArray(left) ? left : [left];
        const r = String(right);
        return leftVals.every(l => !String(l).endsWith(r));
    },
    contains: (left, right) => {
        const leftVals = Array.isArray(left) ? left : [left];
        const r = String(right);
        return leftVals.some(l => String(l).includes(r));
    },
    notContains: (left, right) => {
        const leftVals = Array.isArray(left) ? left : [left];
        const r = String(right);
        return leftVals.every(l => !String(l).includes(r));
    },
    containsAny: (left, arr) => {
        const leftVals = Array.isArray(left) ? left.map(String) : [String(left)];
        const rights = Array.isArray(arr) ? arr.map(String) : [String(arr)];
        return leftVals.some(l => rights.some(r => l.includes(r)));
    },
    notContainsAny: (left, arr) => {
        const leftVals = Array.isArray(left) ? left.map(String) : [String(left)];
        const rights = Array.isArray(arr) ? arr.map(String) : [String(arr)];
        return leftVals.every(l => rights.every(r => !l.includes(r)));
    }
};

function validateQuery(query, options = {}) {
    const { schema } = options;
    const errors = [];
    function err(p, m) { errors.push(`${p}: ${m}`) }
    function isGroupNode(n) { return isObject(n) && (n.rules !== undefined || n.condition !== undefined || n.not !== undefined) }
    function isRuleNode(n) { return isObject(n) && (isString(n.field) || typeof n.field === 'number') && isString(n.operator) }

    function validateGroup(node, path) {
        if (!isArray(node.rules)) { err(`${path}.rules`, 'muss Array sein'); return; }
        node.rules.forEach((c, i) => {
            const p = `${path}.rules[${i}]`;
            if (isGroupNode(c)) validateGroup(c, p);
            else if (isRuleNode(c)) validateRule(c, p);
            else err(p, 'ist weder gültige Regel noch Gruppe');
        });
    }
    function validateRule(node, path) {
        const type = node.type || (schema && schema[node.field] && schema[node.field].type) || 'generic';
        if (!getAllowedOperatorsForType(type).includes(node.operator)) err(`${path}.operator`, `Operator '${node.operator}' nicht erlaubt`);
    }

    if (isGroupNode(query)) validateGroup(query, '$'); else if (isRuleNode(query)) validateRule(query, '$'); else err('$', 'Wurzel ist weder Gruppe noch Regel');
    return { valid: errors.length === 0, errors };
}

function unwrap(val) {
  // Falls PrimeVue/Komponente ein Objekt speichert, nimm die ID/value
  if (val && typeof val === 'object') {
    if ('value' in val) return val.value
    if ('id' in val) return val.id
  }
  return val
}

function evaluateQuery(query, record, options = {}) {
  const v = validateQuery(query, options)
  if (!v.valid) return { matched: false, errors: v.errors }

  function getLeft(rule) {
    let left = record ? record[String(rule.field)] : undefined
    if (Array.isArray(left)) return left.map(unwrap)
    return unwrap(left)
  }

  function evalRule(rule) {
    const fn = OP_EVAL[rule.operator]
    const left = getLeft(rule)
    const right = rule.value
    return fn ? fn(left, right) : false
  }

  function isGroupNode(n){ return n && typeof n==='object' && (n.rules||n.condition||n.not) }
  function evalGroup(g) {
    const cond = (g.condition || 'and').toLowerCase()
    const res = g.rules.map(n => isGroupNode(n) ? evalGroup(n) : evalRule(n))
    const base = cond === 'and' ? res.every(Boolean) : res.some(Boolean)
    return g.not ? !base : base
  }

  const matched = isGroupNode(query) ? evalGroup(query) : evalRule(query)
  return { matched, errors: [] }
}

const WDQuery = { validate: validateQuery, evaluate: evaluateQuery };
export default WDQuery;
 